import {
  Group, Avatar, Text, Container, Paper, Box,
  Badge, Skeleton,
  ActionIcon, Button, TextInput, Textarea,
  Stack, Card, Title, Modal,
  SimpleGrid, ThemeIcon, Menu, Alert,
  Collapse, UnstyledButton, Flex, Divider,
  useMantineTheme,
  List
} from '@mantine/core';
import {
  IconUser, IconMail, IconBuilding,
  IconEdit, IconCheck, IconX, IconCamera,
  IconMapPin,
  IconDots, IconTrash, IconPlus,
  IconReceipt,
  IconActivity, IconChevronDown, IconChevronUp,
  IconAlertCircle,
  IconBrandWhatsapp

} from '@tabler/icons-react';
import { useForm } from '@mantine/form';
import { useDisclosure } from '@mantine/hooks';
import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { notifications } from '@mantine/notifications';
import { AddressService, PaymentService, UserService } from '../../types';
import { truncateTransactionId } from '../../utils/paymentUtils';
import { useProfile } from '../../contexts/ProfileContext';
import PaymentHistory from '../../components/profile/PaymentHistory';

interface Address {
  id: number;
  address: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface User {
  id: string;
  userId: string;
  company_name: string | null;
  first_name: string;
  last_name: string | null;
  tel: string | null;
  email: string | null;
  profilePhoto?: string;
  address: Address[];
}


// Interface matching the PaymentData structure in PaymentHistory component
interface PaymentData {
  id: number;
  transactionId: string;
  amount: number;
  currency: string;
  created_at: string;
  packageId?: number;
  combinedPackageId?: number;
  shipmentId?: number;
  description?: string;
  shipmentOrderId?: string;
  state: string;
}

// Interface for payment history state
interface PaymentHistoryState {
  payments: PaymentData[];
  totalSpent: number;
}

export const Profile = () => {
  const theme = useMantineTheme();
  const navigate = useNavigate();
  const { isProfileComplete, setIsProfileComplete, checkProfileCompletion } = useProfile();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState(false); // Initialize to false and set based on profile status after data loads
  const [photoModalOpened, { open: openPhotoModal, close: closePhotoModal }] = useDisclosure(false);
  const [addressModalOpened, { open: openAddressModal, close: closeAddressModal }] = useDisclosure(false);
  const [paymentHistory, setPaymentHistory] = useState<PaymentHistoryState | null>(null);
  const [paymentLoading, setPaymentLoading] = useState(true);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Mobile collapsible sections state
  const [profileExpanded, setProfileExpanded] = useState(true); // Start expanded to show the important form
  const [addressExpanded, setAddressExpanded] = useState(false);
  const [paymentExpanded, setPaymentExpanded] = useState(false);
  const [activityExpanded, setActivityExpanded] = useState(false);

  // Check if mobile screen
  const isMobile = window.innerWidth < 768;

  // Profile form
  const profileForm = useForm({
    initialValues: {
      first_name: '',
      last_name: '',
      company_name: '',
      tel: '',
      email: ''
    },
    validate: {
      first_name: (value) => (!value ? 'First name is required' : null),
      // Email validation removed as email updates are not allowed
    },
  });

  // Address form
  const addressForm = useForm({
    initialValues: {
      address: '',
      city: '',
      state: '',
      zip: '',
      country: ''
    }
  });

  const fetchUserData = async () => {
    setLoading(true);
    try {
      // Get userInfo from sessionStorage
      const userInfoString = sessionStorage.getItem('userInfo');

      if (userInfoString) {
        // Try to parse with atob first (base64 decode)
        let decodedJson;
        try {
          decodedJson = JSON.parse(atob(userInfoString));
        } catch (e) {
          // If atob fails, try regular JSON parse
          decodedJson = JSON.parse(userInfoString);
        }

        if (decodedJson.sub) {
          const userId = decodedJson.sub;

          const res: any = await UserService.getUserById(userId);


          const userData: User = {
            id: res.id || '',
            userId: res.userId || '',
            company_name: res.company_name || null,
            first_name: res.first_name || '',
            last_name: res.last_name || null,
            email: res.email || null,
            tel: res.tel || null,
            profilePhoto: decodedJson.picture || `https://ui-avatars.com/api/?name=${encodeURIComponent(res.first_name + ' ' + (res.last_name || ''))}`,
            address: Array.isArray(res.address) ? res.address : [],
          };

          setUser(userData);
          profileForm.setValues({
            first_name: userData.first_name,
            last_name: userData.last_name || '',
            company_name: userData.company_name || '',
            tel: userData.tel || '',
            email: userData.email || ''
          });

          // Evaluate profile completion directly here as well
          const isComplete: boolean = !!(
            userData.first_name &&
            userData.first_name.trim() !== '' &&
            userData.last_name &&
            userData.last_name.trim() !== '' &&
            userData.tel &&
            userData.tel.trim() !== ''
          );


          setIsProfileComplete(isComplete);

          // Immediately set edit mode based on profile completion
          setEditMode(!isComplete);
        }
      } else {
        console.error('No userInfoString found in sessionStorage');
      }
    } catch (error) {
      console.error('Error fetching user data:', error);
      notifications.show({
        title: 'Error',
        message: 'Failed to load profile',
        color: 'red',
      });
    } finally {
      setLoading(false);
    }
  };

  /**
   * Fetch user payment history from the API and transform it to match our UI components
   * This function retrieves payment history from the backend and formats it for display
   */
  const fetchPaymentHistory = async () => {
    setPaymentLoading(true);
    try {
      // Fetch payment history using the PaymentService
      const paymentResponse = await PaymentService.getPaymentMyHistory();

      if (paymentResponse.success && paymentResponse.data) {
        // Transform PaymentTransaction data from API to match the PaymentData interface expected by our components
        const transformedPayments: PaymentData[] = paymentResponse.data.map(payment => ({
          id: Number(payment.id) || 0,
          transactionId: payment.transactionId || '',
          amount: Number(payment.amount) || 0,
          currency: 'USD',
          created_at: payment.created_at || new Date().toISOString(),
          // Derive additional fields from available data in PaymentTransaction
          description: `Transaction: ${truncateTransactionId(payment.transactionId || '')}`,
          // Use packageId if it starts with SHP as the shipmentOrderId
          shipmentOrderId: payment.packageId?.toString() ?? undefined,
          state: payment.state || 'UNKNOWN'
        }));

        // Set the transformed payment data in state
        setPaymentHistory({
          payments: transformedPayments,
          totalSpent: transformedPayments.reduce((sum, p) => sum + p.amount, 0)
        });
      } else {
        // Handle empty or error response
        setPaymentHistory({
          payments: [],
          totalSpent: 0
        });
      }
    } catch (error) {
      console.error('Error fetching payment history:', error);
      setPaymentHistory({
        payments: [],
        totalSpent: 0
      });
    } finally {
      setPaymentLoading(false);
    }
  };


  const handleUpdateProfile = async (values: typeof profileForm.values) => {
    try {
      if (!user?.id) return;

      // Spread the user object to include all properties and override with new values
      // Explicitly exclude email from the values to be updated
      const { email, ...updateValues } = values;

      await UserService.updateUser(user.id, {
        ...user,
        ...updateValues,
        email: user.email || undefined
      });

      setUser(prev => prev ? { ...prev, ...updateValues } : null);

      // Check if profile is now complete
      const isComplete: boolean = !!(
        updateValues.first_name?.trim() !== '' &&
        updateValues.last_name?.trim() !== '' &&
        updateValues.tel?.trim() !== ''
      );

      // Update profile completion status
      setIsProfileComplete(isComplete);

      // Only exit edit mode if profile is complete
      if (isComplete) {
        setEditMode(false);
      }

      notifications.show({
        title: 'Success',
        message: isComplete
          ? 'Profile completed successfully'
          : 'Profile updated successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to update profile',
        color: 'red',
      });
    }
  };

  const handleAddAddress = async (values: typeof addressForm.values) => {
    try {
      if (!user) return;

      const newAddress = {
        id: Date.now(),
        ...values
      };

      const updatedAddresses = [...user.address, newAddress];

      // Need to use the exact property name expected by the API
      await AddressService.postAddress({
        address: values.address,
        city: values.city,
        state: values.state,
        zip: values.zip,
        country: values.country
      });

      setUser(prev => prev ? { ...prev, address: updatedAddresses } : null);
      addressForm.reset();
      closeAddressModal();

      notifications.show({
        title: 'Success',
        message: 'Address added successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to add address',
        color: 'red',
      });
    }
  };

  const handleDeleteAddress = async (addressId: number) => {
    try {
      if (!user) return;

      const updatedAddresses = user.address.filter(addr => addr.id !== addressId);

      await AddressService.deleteAddress(addressId.toString());

      setUser(prev => prev ? { ...prev, address: updatedAddresses } : null);

      notifications.show({
        title: 'Success',
        message: 'Address deleted successfully',
        color: 'green',
      });
    } catch (error) {
      notifications.show({
        title: 'Error',
        message: 'Failed to delete address',
        color: 'red',
      });
    }
  };

  const handlePhotoUpload = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];

      if (file.size > 2 * 1024 * 1024) {
        notifications.show({
          title: 'Error',
          message: 'Image size should be less than 2MB',
          color: 'red',
        });
        return;
      }

      const reader = new FileReader();
      reader.onload = (event) => {
        if (event.target?.result && user) {
          setUser(prev => prev ? {
            ...prev,
            profilePhoto: event.target?.result as string
          } : null);

          notifications.show({
            title: 'Success',
            message: 'Profile photo updated',
            color: 'green',
          });
        }
      };
      reader.readAsDataURL(file);
    }
  };

  useEffect(() => {
    // First, check profile completion status again
    checkProfileCompletion();
    // Then fetch user data
    fetchUserData();
    fetchPaymentHistory();

    // Add a safety check in case the profile context hasn't loaded properly
    const safetyTimeout = setTimeout(() => {
      if (!user) {

        fetchUserData();
      }
    }, 2000);

    return () => clearTimeout(safetyTimeout);
  }, []);

  // Update editMode when profile completion status changes
  useEffect(() => {

    if (!isProfileComplete) {
      setEditMode(true);
      // Auto-expand profile section on mobile if profile is incomplete
      setProfileExpanded(true);
    }
  }, [isProfileComplete]);

  // This effect ensures UI state is consistent after user data is loaded
  useEffect(() => {
    if (user) {
      const isComplete: boolean = !!(
        user.first_name &&
        user.first_name.trim() !== '' &&
        user.last_name &&
        user.last_name.trim() !== '' &&
        user.tel &&
        user.tel.trim() !== ''
      );


      setIsProfileComplete(isComplete);
      setEditMode(!isComplete);
    }
  }, [user]);

  if (loading) {
    return (
      <Container size="lg" px="md">
        <Stack gap="md">
          <Skeleton height={200} radius="md" />
          <SimpleGrid cols={{ base: 2, sm: 4 }}>
            {Array(4).fill(0).map((_, i) => (
              <Skeleton key={i} height={80} radius="md" />
            ))}
          </SimpleGrid>
          <Skeleton height={300} radius="md" />
        </Stack>
      </Container>
    );
  }

  if (!user) {
    return (
      <Container size="lg" px="md">
        <Alert color="red" title="Error" icon={<IconX size={16} />}>
          Failed to load user profile. Please try again.
        </Alert>
      </Container>
    );
  }

  return (
    <Container size={isMobile ? "sm" : "lg"} px={isMobile ? "xs" : "md"} py={isMobile ? "xs" : "md"}>
      <Stack gap={isMobile ? "sm" : "lg"}>
        {!isProfileComplete && (
          <Alert
            icon={<IconAlertCircle size="1.1rem" />}
            title="Welcome to OTL Express!"
            color='teal'
            radius="md"
            variant="filled"
            styles={{
              title: {
                fontSize: '1.1rem',
                fontWeight: 600,
                color: 'yellow'
              },
              label: {
                color: 'yellow'
              }
            }}
          >
            <Text mb="xs" fw={500} c="white">
              Please complete your profile information to get started.
            </Text>
            <List size="sm" spacing="xs" withPadding styles={{ item: { color: 'white' } }}>
              <List.Item icon={
                <ThemeIcon color="cyan" size={18} radius="xl">
                  <IconAlertCircle size="0.8rem" />
                </ThemeIcon>
              }>
                First Name (Required)
              </List.Item>
              <List.Item icon={
                <ThemeIcon color="cyan" size={18} radius="xl">
                  <IconAlertCircle size="0.8rem" />
                </ThemeIcon>
              }>
                Last Name (Required)
              </List.Item>
              <List.Item icon={
                <ThemeIcon color="cyan" size={18} radius="xl">
                  <IconAlertCircle size="0.8rem" />
                </ThemeIcon>
              }>
                Phone Number (Required)
              </List.Item>
            </List>
            <Text mt="xs" size="sm" fw={500} c="white" opacity={0.9}>
              These details are necessary to ensure smooth delivery of your packages.
            </Text>
          </Alert>
        )}
        {/* Collapsible Profile Section - Mobile */}
        {isMobile ? (
          <div>
            <Paper radius="xl" p="md" bg="gradient-to-r from-blue-500 to-purple-600" style={{ color: 'white' }}>
              <Flex align="center" gap="sm" mb="sm">
                <Avatar
                  src={user.profilePhoto}
                  size="md"
                  radius="xl"
                  style={{ border: '2px solid white' }}
                />
                <Box flex={1}>
                  <Text fw={700} size="md" lineClamp={1} c={"blue"}>
                    {user.first_name} {user.last_name}
                  </Text>
                  <Text size="xs" opacity={0.8} lineClamp={1} c={"blue"}>
                    {user.email}
                  </Text>
                </Box>
              </Flex>
            </Paper>
            <Paper radius="md" withBorder>
              <UnstyledButton
                w="100%"
                p="md"
                onClick={() => setProfileExpanded(!profileExpanded)}
                style={{
                  borderRadius: theme.radius.md,
                  transition: 'background-color 0.2s',
                }}
              >
                <Group justify="space-between">
                  <Group gap="sm">
                    <ThemeIcon size="sm" variant="light" color="blue">
                      <IconUser size={16} />
                    </ThemeIcon>
                    <Text fw={500}>Profile Information</Text>
                  </Group>
                  <ActionIcon variant="subtle" size="sm">
                    {profileExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                  </ActionIcon>
                </Group>
              </UnstyledButton>

              <Collapse in={profileExpanded}>
                <Divider />
                <Box p="md">
                  {editMode ? (
                    <form onSubmit={profileForm.onSubmit(handleUpdateProfile)}>
                      <Stack gap="sm">
                        <TextInput
                          label={
                            <Group gap={4}>
                              <Text size="sm">First Name</Text>
                            </Group>
                          }
                          size="sm"
                          {...profileForm.getInputProps('first_name')}
                          required
                        />
                        <TextInput
                          label={
                            <Group gap={4}>
                              <Text size="sm">Last Name</Text>
                            </Group>
                          }
                          size="sm"
                          {...profileForm.getInputProps('last_name')}
                          required
                        />
                        <TextInput
                          label="Company"
                          size="sm"
                          {...profileForm.getInputProps('company_name')}
                        />
                        <TextInput
                          label="Email"
                          size="sm"
                          {...profileForm.getInputProps('email')}
                          readOnly
                          description="Email cannot be updated"
                        />
                        <TextInput
                          label={
                            <Group gap={4}>
                              <Group gap={4}>
                                <IconBrandWhatsapp size={14} />
                                <Text size="sm">Phone</Text>
                              </Group>
                            </Group>
                          }
                          size="sm"
                          {...profileForm.getInputProps('tel')}
                          required
                        />
                        <Group gap="xs">
                          <Button type="submit" size="sm" leftSection={<IconCheck size={14} />}>
                            Save
                          </Button>
                          <Button
                            variant="outline"
                            size="sm"
                            leftSection={<IconX size={14} />}
                            onClick={() => {
                              setEditMode(false);
                              profileForm.reset();
                            }}
                          >
                            Cancel
                          </Button>
                        </Group>
                      </Stack>
                    </form>
                  ) : (
                    <Stack gap="sm">
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Name</Text>
                        <Text size="sm" fw={500}>{user.first_name} {user.last_name}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Email</Text>
                        <Text size="sm" fw={500} lineClamp={1}>{user.email}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Phone</Text>
                        <Text size="sm" fw={500}>{user.tel || 'Not set'}</Text>
                      </Group>
                      <Group justify="space-between">
                        <Text size="sm" c="dimmed">Company</Text>
                        <Text size="sm" fw={500}>{user.company_name || 'Not set'}</Text>
                      </Group>
                      <Button
                        size="sm"
                        variant="light"
                        leftSection={<IconEdit size={14} />}
                        onClick={() => setEditMode(true)}
                        fullWidth
                        mt="xs"
                      >
                        Edit Profile
                      </Button>
                    </Stack>
                  )}
                </Box>
              </Collapse>
            </Paper>
          </div>
        ) : (
          /* Desktop Profile Information */
          <Card radius="lg" p="lg">
            <Group justify="space-between" mb="lg">
              <Group>
                <Box pos="relative">
                  <Avatar
                    src={user.profilePhoto}
                    size={80}
                    radius="xl"
                    style={{ border: '3px solid white' }}
                  />
                  <ActionIcon
                    size="sm"
                    radius="xl"
                    bg="white"
                    c="dark"
                    pos="absolute"
                    bottom={0}
                    right={0}
                    onClick={openPhotoModal}
                  >
                    <IconCamera size={12} />
                  </ActionIcon>
                </Box>
                <Box>
                  <Title order={2}>
                    {user.first_name} {user.last_name}
                  </Title>
                  <Text opacity={0.8}>
                    {user.email}
                  </Text>
                  <Group gap="xs" mt="xs">
                    <Badge variant="light">
                      ID: {user.userId}
                    </Badge>
                  </Group>
                </Box>
              </Group>
              {!editMode && (
                <Button
                  leftSection={<IconEdit size={16} />}
                  variant="light"
                  onClick={() => setEditMode(true)}
                >
                  Edit Profile
                </Button>
              )}
            </Group>

            {editMode ? (
              <form onSubmit={profileForm.onSubmit(handleUpdateProfile)}>
                <Stack gap="md">
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <TextInput
                      label={
                        <Group gap={4}>
                          <Text>First Name</Text>
                        </Group>
                      }
                      placeholder="Enter first name"
                      {...profileForm.getInputProps('first_name')}
                      required
                    />
                    <TextInput
                      label={
                        <Group gap={4}>
                          <Text>Last Name</Text>
                        </Group>
                      }
                      placeholder="Enter last name"
                      {...profileForm.getInputProps('last_name')}
                      required
                    />
                  </SimpleGrid>
                  <TextInput
                    label="Company"
                    placeholder="Enter company name"
                    {...profileForm.getInputProps('company_name')}
                  />
                  <SimpleGrid cols={{ base: 1, sm: 2 }}>
                    <TextInput
                      label="Email"
                      placeholder="Enter email"
                      {...profileForm.getInputProps('email')}
                      readOnly
                      description="Email address cannot be updated"
                    />
                    <TextInput
                      label={
                        <Group gap={4}>
                          <Text>Phone</Text>
                        </Group>
                      }
                      placeholder="Enter WhatsApp phone number"
                      leftSection={<IconBrandWhatsapp size={16} />}
                      {...profileForm.getInputProps('tel')}
                      required
                    />
                  </SimpleGrid>
                  <Group>
                    <Button type="submit" leftSection={<IconCheck size={16} />}>
                      Save Changes
                    </Button>
                    <Button
                      variant="outline"
                      leftSection={<IconX size={16} />}
                      onClick={() => {
                        setEditMode(false);
                        profileForm.reset();
                      }}
                    >
                      Cancel
                    </Button>
                  </Group>
                </Stack>
              </form>
            ) : (
              <Stack mt="xl">
                <SimpleGrid cols={{ base: 1, sm: 2 }}>
                  <Box>
                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                      <IconUser size={16} style={{ display: 'inline', marginRight: 8 }} />
                      Full Name
                    </Text>
                    <Text>{user.first_name} {user.last_name}</Text>
                  </Box>
                  <Box>
                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                      <IconMail size={16} style={{ display: 'inline', marginRight: 8 }} />
                      Email
                    </Text>
                    <Text>{user.email}</Text>
                  </Box>
                  <Box>
                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                      <IconBuilding size={16} style={{ display: 'inline', marginRight: 8 }} />
                      Company
                    </Text>
                    <Text>{user.company_name || 'Not specified'}</Text>
                  </Box>
                  <Box>
                    <Text size="sm" fw={500} c="dimmed" mb={4}>
                      <IconBrandWhatsapp size={16} style={{ display: 'inline', marginRight: 8 }} />
                      Phone
                    </Text>
                    <Text>{user.tel || 'Not specified'}</Text>
                  </Box>
                </SimpleGrid>
              </Stack>
            )}
          </Card>
        )}

        {/* Collapsible Address Section - Mobile */}
        {isMobile ? (
          <Paper radius="md" withBorder>
            <UnstyledButton
              w="100%"
              p="md"
              onClick={() => setAddressExpanded(!addressExpanded)}
              style={{
                borderRadius: theme.radius.md,
                transition: 'background-color 0.2s',
              }}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <ThemeIcon size="sm" variant="light" color="green">
                    <IconMapPin size={16} />
                  </ThemeIcon>
                  <Text fw={500}>Addresses ({user.address.length})</Text>
                </Group>
                <ActionIcon variant="subtle" size="sm">
                  {addressExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </ActionIcon>
              </Group>
            </UnstyledButton>

            <Collapse in={addressExpanded}>
              <Divider />
              <Box p="md">
                <Button
                  size="sm"
                  variant="light"
                  leftSection={<IconPlus size={14} />}
                  onClick={openAddressModal}
                  fullWidth
                  mb="sm"
                >
                  Add Address
                </Button>

                {user.address.length === 0 ? (
                  <Text size="sm" c="dimmed" ta="center" py="md">
                    No addresses saved yet
                  </Text>
                ) : (
                  <Stack gap="sm">
                    {user.address.map((address, index) => (
                      <Paper key={address.id} p="sm" radius="sm" bg="gray.0">
                        <Group justify="space-between" mb="xs">
                          <Text size="sm" fw={500} lineClamp={1}>
                            Address {index + 1}
                          </Text>
                          <ActionIcon
                            size="sm"
                            variant="subtle"
                            color="red"
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            <IconTrash size={12} />
                          </ActionIcon>
                        </Group>
                        <Text size="xs" c="dimmed" lineClamp={2}>
                          {address.address}, {address.city}, {address.state} {address.zip}, {address.country}
                        </Text>
                      </Paper>
                    ))}
                  </Stack>
                )}
              </Box>
            </Collapse>
          </Paper>
        ) : (
          /* Desktop Addresses */
          <Card radius="lg" p="lg">
            <Group justify="space-between" mb="lg">
              <Title order={3}>Saved Addresses</Title>
              <Button
                leftSection={<IconPlus size={16} />}
                variant="light"
                onClick={openAddressModal}
              >
                Add Address
              </Button>
            </Group>

            {user.address.length === 0 ? (
              <Paper p="xl" radius="md" bg="gray.0" ta="center">
                <ThemeIcon size="xl" variant="light" color="gray" mx="auto" mb="md">
                  <IconMapPin size={32} />
                </ThemeIcon>
                <Text c="dimmed">No addresses saved yet</Text>
                <Button variant="light" mt="md" onClick={openAddressModal}>
                  Add your first address
                </Button>
              </Paper>
            ) : (
              <SimpleGrid cols={{ base: 1, sm: 2 }}>
                {user.address.map((address) => (
                  <Paper key={address.id} p="md" radius="md" withBorder>
                    <Group justify="space-between" mb="xs">
                      <ThemeIcon size="sm" variant="light" color="blue">
                        <IconMapPin size={14} />
                      </ThemeIcon>
                      <Menu>
                        <Menu.Target>
                          <ActionIcon variant="subtle" size="sm">
                            <IconDots size={16} />
                          </ActionIcon>
                        </Menu.Target>
                        <Menu.Dropdown>
                          <Menu.Item
                            leftSection={<IconTrash size={14} />}
                            color="red"
                            onClick={() => handleDeleteAddress(address.id)}
                          >
                            Delete
                          </Menu.Item>
                        </Menu.Dropdown>
                      </Menu>
                    </Group>
                    <Text fw={500} mb="xs">{address.address}</Text>
                    <Text size="sm" c="dimmed">
                      {address.city}, {address.state} {address.zip}
                    </Text>
                    <Text size="sm" c="dimmed">{address.country}</Text>
                  </Paper>
                ))}
              </SimpleGrid>
            )}
          </Card>
        )}

        {/* Collapsible Payment History - Mobile */}
        {isMobile ? (
          <Paper radius="md" withBorder>
            <UnstyledButton
              w="100%"
              p="md"
              onClick={() => setPaymentExpanded(!paymentExpanded)}
              style={{
                borderRadius: theme.radius.md,
                transition: 'background-color 0.2s',
              }}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <ThemeIcon size="sm" variant="light" color="orange">
                    <IconReceipt size={16} />
                  </ThemeIcon>
                  <Text fw={500}>Payment History</Text>
                </Group>
                <ActionIcon variant="subtle" size="sm">
                  {paymentExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </ActionIcon>
              </Group>
            </UnstyledButton>

            <Collapse in={paymentExpanded}>
              <Divider />
              <Box p="md">
                <Button
                  size="sm"
                  variant="light"
                  rightSection={<IconReceipt size={14} />}
                  onClick={() => navigate('/billing')}
                  fullWidth
                  mb="sm"
                >
                  View Full Billing
                </Button>

                {paymentLoading ? (
                  <Skeleton height={100} radius="sm" />
                ) : (
                  <PaymentHistory
                    payments={paymentHistory?.payments || []}
                    showViewAll={!!(paymentHistory && paymentHistory.payments.length > 3)}
                    onViewAll={() => navigate('/billing')}
                    isLoading={false}
                  />
                )}
              </Box>
            </Collapse>
          </Paper>
        ) : (
          /* Desktop Payment History */
          <Card radius="lg" p="lg">
            <Group justify="space-between" mb="md">
              <Title order={3}>Payment History</Title>
              <Button
                variant="light"
                onClick={() => navigate('/billing')}
                rightSection={<IconReceipt size={16} />}
              >
                View Billing
              </Button>
            </Group>

            {paymentLoading ? (
              <Skeleton height={200} radius="md" />
            ) : (
              <PaymentHistory
                payments={paymentHistory?.payments || []}
                showViewAll={!!(paymentHistory && paymentHistory.payments.length > 5)}
                onViewAll={() => navigate('/billing')}
                isLoading={paymentLoading}
              />
            )}
          </Card>
        )}

        {/* Collapsible Activity Section - Mobile */}
        {isMobile ? (
          <Paper radius="md" withBorder>
            <UnstyledButton
              w="100%"
              p="md"
              onClick={() => setActivityExpanded(!activityExpanded)}
              style={{
                borderRadius: theme.radius.md,
                transition: 'background-color 0.2s',
              }}
            >
              <Group justify="space-between">
                <Group gap="sm">
                  <ThemeIcon size="sm" variant="light" color="violet">
                    <IconActivity size={16} />
                  </ThemeIcon>
                  <Text fw={500}>Account Activity</Text>
                </Group>
                <ActionIcon variant="subtle" size="sm">
                  {activityExpanded ? <IconChevronUp size={16} /> : <IconChevronDown size={16} />}
                </ActionIcon>
              </Group>
            </UnstyledButton>

            <Collapse in={activityExpanded}>
              <Divider />
              <Box p="md">
                <Button
                  size="sm"
                  variant="light"
                  rightSection={<IconActivity size={14} />}
                  onClick={() => navigate('/activity')}
                  fullWidth
                  mb="sm"
                >
                  View Full Activity
                </Button>

                <Text size="sm" c="dimmed" ta="center" py="md">
                  No recent activity
                </Text>
              </Box>
            </Collapse>
          </Paper>
        ) : (
          /* Desktop Activity */
          <Card radius="lg" p="lg">
            <Group justify="space-between" mb="md">
              <Title order={3}>Account Activity</Title>
              <Button
                variant="light"
                onClick={() => navigate('/activity')}
                rightSection={<IconActivity size={16} />}
              >
                View Activity
              </Button>
            </Group>

            <Paper p="xl" radius="md" bg="gray.0" ta="center">
              <ThemeIcon size="xl" variant="light" color="gray" mx="auto" mb="md">
                <IconActivity size={32} />
              </ThemeIcon>
              <Text c="dimmed">No recent activity</Text>
            </Paper>
          </Card>
        )}

        {/* Desktop sections for non-mobile */}
        {!isMobile && (
          <>
            {/* ...existing desktop Quick Actions... */}
            {/* ...existing desktop Profile Information... */}
            {/* ...existing desktop Addresses... */}
            {/* ...existing desktop Payment History... */}
            {/* ...existing desktop Account Activity... */}
          </>
        )}

        {/* Photo Modal - Optimized for Mobile */}
        <Modal
          opened={photoModalOpened}
          onClose={closePhotoModal}
          title="Update Profile Photo"
          centered
          size={isMobile ? "xs" : "sm"}
          padding={isMobile ? "sm" : "md"}
        >
          <Stack gap={isMobile ? "sm" : "md"}>
            <Text size="sm" c="dimmed">
              Choose a new profile photo. Maximum file size is 2MB.
            </Text>
            <input
              type="file"
              ref={fileInputRef}
              style={{ display: 'none' }}
              accept="image/png,image/jpeg,image/jpg"
              onChange={handlePhotoUpload}
            />
            <Button
              fullWidth
              size={isMobile ? "sm" : "md"}
              leftSection={<IconCamera size={isMobile ? 14 : 16} />}
              onClick={() => fileInputRef.current?.click()}
            >
              Choose Photo
            </Button>
          </Stack>
        </Modal>

        {/* Address Modal - Optimized for Mobile */}
        <Modal
          opened={addressModalOpened}
          onClose={closeAddressModal}
          title="Add New Address"
          centered
          size={isMobile ? "xs" : "md"}
          padding={isMobile ? "sm" : "md"}
        >
          <form onSubmit={addressForm.onSubmit(handleAddAddress)}>
            <Stack gap={isMobile ? "sm" : "md"}>
              <Textarea
                label="Street Address"
                placeholder="Enter street address"
                rows={2}
                size={isMobile ? "sm" : "md"}
                {...addressForm.getInputProps('address')}
              />
              <SimpleGrid cols={isMobile ? 1 : 2} spacing={isMobile ? "sm" : "md"}>
                <TextInput
                  label="City"
                  placeholder="Enter city"
                  size={isMobile ? "sm" : "md"}
                  {...addressForm.getInputProps('city')}
                />
                <TextInput
                  label="State/Province"
                  placeholder="Enter state"
                  size={isMobile ? "sm" : "md"}
                  {...addressForm.getInputProps('state')}
                />
              </SimpleGrid>
              <SimpleGrid cols={isMobile ? 1 : 2} spacing={isMobile ? "sm" : "md"}>
                <TextInput
                  label="ZIP/Postal Code"
                  placeholder="Enter ZIP"
                  size={isMobile ? "sm" : "md"}
                  {...addressForm.getInputProps('zip')}
                />
                <TextInput
                  label="Country"
                  placeholder="Enter country"
                  size={isMobile ? "sm" : "md"}
                  {...addressForm.getInputProps('country')}
                />
              </SimpleGrid>
              <Group justify="flex-end" gap={isMobile ? "xs" : "sm"}>
                <Button
                  variant="outline"
                  onClick={closeAddressModal}
                  size={isMobile ? "sm" : "md"}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  leftSection={<IconCheck size={isMobile ? 14 : 16} />}
                  size={isMobile ? "sm" : "md"}
                >
                  Add Address
                </Button>
              </Group>
            </Stack>
          </form>
        </Modal>
      </Stack>
    </Container >
  );
};

export default Profile;
